import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Modal, Menu, MenuItem, Tooltip, useTheme } from '@mui/material';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import OpenInFullIcon from 'packages/novous_web/src/assets/icons/Expand.svg';
import EditIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import CollapseIcon from 'packages/novous_web/src/assets/icons/CollapseIcon.svg';
import RestartAltIcon from 'packages/novous_web/src/assets/icons/Reset.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface HeatMapData {
  vehicleId: string;
  day: string;
  value: number;
  displayValue: string;
  sortDate: string;
  idle_time?: number;
  record_date?: string;
}

interface TooltipData {
  vehicleId: string;
  day: string;
  value: string;
  idleTime: string;
  x: number;
  y: number;
}

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
}

interface ProductivityHeatMapChartProps {
  title?: string;
  data?: HeatMapData[];
  containerClassName?: string;
  infoIcon?: React.ReactNode;
  tableTitle?: string;
  tableHeaderText?: string;
  tableColumns?: TableColumn[];
  tableData?: any[];
  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
  chartId?: number;
  filters?: any;
  onTableIconClick?: () => void;
  paginationInfo?: any;
  onPaginationChange?: (cursor: any) => void;
  // Server-side day pagination (heatmap)
  dayPaginationInfo?: any;
  onDayPaginationChange?: (cursor: any) => void;
}

const ProductivityHeatMapChart: React.FC<ProductivityHeatMapChartProps> = ({
  title = 'Idle Time by Vehicle',
  data: initialData = [],
  containerClassName = '',
  infoIcon,
  tableTitle = 'Idle Time Details',
  tableHeaderText = 'Detailed breakdown of idle time by vehicle and date',
  tableColumns = [
    { key: 'vehicleId', label: 'Vehicle ID', align: 'left' },
    { key: 'record_date', label: 'Date', align: 'center' },
    { key: 'displayValue', label: 'Idle Time (hours)', align: 'right' },
  ],
  tableData = [],
  onCardDataUpdate,
  chartId,
  filters,
  onTableIconClick,
  paginationInfo,
  onPaginationChange,
  // New props for server-side day pagination (heatmap-specific)
  dayPaginationInfo,
  onDayPaginationChange,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);
  const windowStart = useRef(0);
  const [dataRangeLabel, setDataRangeLabel] = useState('');
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [paginationTrigger, setPaginationTrigger] = useState(0);

  // Normalize incoming data to a flat list of { vehicleId, day, value, displayValue, sortDate }
  const data = useMemo(() => {
    if (!initialData || initialData.length === 0) return [];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const flattened: HeatMapData[] = [];

    const firstItem: any = initialData[0];
    const looksLikeGroupedArray =
      firstItem && (Array.isArray(firstItem.record_date) || Array.isArray(firstItem.idle_time_hours));

    if (looksLikeGroupedArray) {
      // Each item is a vehicle with arrays of dates and values
      (initialData as any[]).forEach((veh) => {
        const vehicleId = veh.vehicle_id || veh.vehicleId || '';
        const dates = Array.isArray(veh.record_date) ? veh.record_date : [];
        const values = Array.isArray(veh.idle_time_hours) ? veh.idle_time_hours : [];

        for (let i = 0; i < Math.max(dates.length, values.length); i++) {
          const date = dates[i];
          const val = values[i] ?? 0;
          let dayLabel = '';
          try {
            if (date) {
              const d = new Date(date);
              dayLabel = `${dayNames[d.getDay()]} ${d.getDate().toString().padStart(2, '0')}`;
            }
          } catch (e) {
            dayLabel = date || '';
          }

          flattened.push({
            vehicleId,
            day: dayLabel,
            value: Number(val),
            displayValue: Number(val).toFixed(1),
            sortDate: date || '',
            record_date: date || '',
            idle_time: val,
          });
        }
      });

      console.log('✅ Flattened heatmap data (per vehicle/day):', flattened);
    } else {
      // Already flat, ensure types
      (initialData as HeatMapData[]).forEach((item) => {
        flattened.push({
          vehicleId: item.vehicleId,
          day: item.day,
          value: item.value,
          displayValue: item.displayValue || String(item.value),
          sortDate: item.sortDate || item.record_date || '',
          record_date: item.record_date,
          idle_time: item.idle_time,
        });
      });
    }

    // Group by vehicleId and take first 7 vehicles
    const vehicleGroups: { [key: string]: HeatMapData[] } = {};
    flattened.forEach((item) => {
      if (!vehicleGroups[item.vehicleId]) vehicleGroups[item.vehicleId] = [];
      vehicleGroups[item.vehicleId].push(item);
    });

    const vehicleIds = Object.keys(vehicleGroups).slice(0, 7);
    const result: HeatMapData[] = [];
    vehicleIds.forEach((vehicleId) => result.push(...vehicleGroups[vehicleId]));

    return result;
  }, [initialData]);

  // Get unique vehicles from data and sort them
  const allVehicles = useMemo(() => {
    const uniqueVehicles = Array.from(new Set(data.map((d) => d.vehicleId)));
    return uniqueVehicles.sort();
  }, [data]);

  // Get unique days from data and sort them
  const allDays = useMemo(() => {
    const dayToDateMap = new Map<string, string>();
    data.forEach((d) => {
      if (!dayToDateMap.has(d.day) && d.sortDate) {
        dayToDateMap.set(d.day, d.sortDate);
      }
    });

    const uniqueDays = Array.from(new Set(data.map((d) => d.day))).sort((a, b) => {
      const dateA = dayToDateMap.get(a);
      const dateB = dayToDateMap.get(b);

      if (dateA && dateB) {
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }

      const dayA = parseInt(a.split(' ')[1]);
      const dayB = parseInt(b.split(' ')[1]);
      return dayB - dayA;
    });
    return uniqueDays;
  }, [data]);

  // Pagination settings
  const daysPerPage = 7;
  const [currentDays, setCurrentDays] = useState<string[]>([]);
  const prevAllDaysLength = useRef(0);

  // Server-side pagination state
  const [serverOffset, setServerOffset] = useState<number>(0);
  // When dayPaginationInfo + onDayPaginationChange are provided, use server-side paging for days
  const isServerPaging = Boolean(dayPaginationInfo && onDayPaginationChange);

  // First, add state and refs for page tracking (add these with other state declarations)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageHistory = useRef<any[]>([]);
  const cursorStack = useRef<any[]>([]);

  // Then update the useEffect that handles pagination:
  useEffect(() => {
    if (isServerPaging) {
      // Server-side: use the days present in the current dataset
      const uniqueDays = Array.from(new Set(data.map((d) => d.day)));
      // Try to preserve order similar to client-side sort
      uniqueDays.sort((a: string, b: string) => {
        const getSortDate = (label: string) => {
          const d = data.find((x) => x.day === label);
          return d ? d.sortDate : '';
        };
        const dateA = getSortDate(a);
        const dateB = getSortDate(b);
        if (dateA && dateB) return new Date(dateB).getTime() - new Date(dateA).getTime();
        const dayA = parseInt(a.split(' ')[1] || '0');
        const dayB = parseInt(b.split(' ')[1] || '0');
        return dayB - dayA;
      });

      setCurrentDays(uniqueDays);

      // Calculate page numbers for server-side pagination
      if (dayPaginationInfo) {
        const pageSize = dayPaginationInfo.pageSize || 7;
        const totalRecords = dayPaginationInfo.totalRecords || 0;

        // Calculate current page based on offset
        const currentOffset = serverOffset || 0;
        const calculatedPage = Math.floor(currentOffset / pageSize) + 1;
        const calculatedTotalPages = Math.ceil(totalRecords / pageSize);

        setCurrentPage(calculatedPage);
        setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);
      }

      // Determine hasNext/hasPrevious from paginationInfo and serverOffset
      const nextCursor =
        dayPaginationInfo && (dayPaginationInfo.lastEvaluatedKey || dayPaginationInfo.next_cursor);
      setHasNext(!!nextCursor);

      // Try to infer current offset from server-provided next cursor
      if (nextCursor && typeof nextCursor.offset === 'number') {
        const inferredOffset = Math.max(0, (nextCursor.offset || 0) - uniqueDays.length);
        setServerOffset(inferredOffset);
        setHasPrevious(inferredOffset > 0);
      } else {
        setHasPrevious(serverOffset > 0);
      }

      return; // skip client-side pagination logic
    }

    if (allDays.length > 0) {
      const allDaysLengthChanged = prevAllDaysLength.current !== allDays.length;
      prevAllDaysLength.current = allDays.length;

      if (allDaysLengthChanged) {
        windowStart.current = Math.max(0, allDays.length - daysPerPage);
      }

      const currentWindow = allDays.slice(windowStart.current, windowStart.current + daysPerPage);
      setCurrentDays(currentWindow);

      // Calculate current page for client-side pagination
      const calculatedPage = Math.floor(windowStart.current / daysPerPage) + 1;
      const calculatedTotalPages = Math.ceil(allDays.length / daysPerPage);

      setCurrentPage(calculatedPage);
      setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);

      setHasPrevious(windowStart.current > 0);
      setHasNext(windowStart.current + daysPerPage < allDays.length);
    } else {
      windowStart.current = 0;
      setCurrentDays([]);
      setCurrentPage(1);
      setTotalPages(1);
      setHasPrevious(false);
      setHasNext(false);
    }
  }, [allDays, paginationTrigger, daysPerPage, dayPaginationInfo, isServerPaging, data, serverOffset]);

  // Update handlePrevious function:
  const handlePrevious = () => {
    if (isServerPaging) {
      // compute previous offset and request
      const prevOffset = Math.max(0, serverOffset - (currentDays.length || daysPerPage));
      if (onDayPaginationChange) {
        onDayPaginationChange({ offset: prevOffset });
        setServerOffset(prevOffset);
        setCurrentPage(Math.max(1, currentPage - 1));
      }
      return;
    }

    if (hasPrevious) {
      windowStart.current = Math.max(0, windowStart.current - daysPerPage);
      setPaginationTrigger((prev) => prev + 1);
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  };

  // Update handleNext function:
  const handleNext = () => {
    if (isServerPaging) {
      const nextCursor =
        (dayPaginationInfo && (dayPaginationInfo.lastEvaluatedKey || dayPaginationInfo.next_cursor)) ?? null;
      if (nextCursor && onDayPaginationChange) {
        onDayPaginationChange(nextCursor);
        if (typeof nextCursor.offset === 'number') setServerOffset(nextCursor.offset);
        setCurrentPage(currentPage + 1);
      }
      return;
    }

    if (hasNext) {
      windowStart.current = Math.min(allDays.length - daysPerPage, windowStart.current + daysPerPage);
      setPaginationTrigger((prev) => prev + 1);
      setCurrentPage(currentPage + 1);
    }
  };

  // Effect to handle pagination
  useEffect(() => {
    if (isServerPaging) {
      // Server-side: use the days present in the current dataset
      const uniqueDays = Array.from(new Set(data.map((d) => d.day)));
      // Try to preserve order similar to client-side sort
      uniqueDays.sort((a: string, b: string) => {
        const getSortDate = (label: string) => {
          const d = data.find((x) => x.day === label);
          return d ? d.sortDate : '';
        };
        const dateA = getSortDate(a);
        const dateB = getSortDate(b);
        if (dateA && dateB) return new Date(dateB).getTime() - new Date(dateA).getTime();
        const dayA = parseInt(a.split(' ')[1] || '0');
        const dayB = parseInt(b.split(' ')[1] || '0');
        return dayB - dayA;
      });

      setCurrentDays(uniqueDays);

      if (uniqueDays.length > 0) {
        const startLabel = uniqueDays[0];
        const endLabel = uniqueDays[uniqueDays.length - 1];
        const rangeLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
        setDataRangeLabel(rangeLabel);
      } else {
        setDataRangeLabel('');
      }

      // Determine hasNext/hasPrevious from paginationInfo and serverOffset
      const nextCursor =
        dayPaginationInfo && (dayPaginationInfo.lastEvaluatedKey || dayPaginationInfo.next_cursor);
      setHasNext(!!nextCursor);

      // Try to infer current offset from server-provided next cursor
      if (nextCursor && typeof nextCursor.offset === 'number') {
        const inferredOffset = Math.max(0, (nextCursor.offset || 0) - uniqueDays.length);
        setServerOffset(inferredOffset);
        setHasPrevious(inferredOffset > 0);
      } else {
        setHasPrevious(serverOffset > 0);
      }

      return; // skip client-side pagination logic
    }

    if (allDays.length > 0) {
      const allDaysLengthChanged = prevAllDaysLength.current !== allDays.length;
      prevAllDaysLength.current = allDays.length;

      if (allDaysLengthChanged) {
        windowStart.current = Math.max(0, allDays.length - daysPerPage);
      }

      const currentWindow = allDays.slice(windowStart.current, windowStart.current + daysPerPage);
      setCurrentDays(currentWindow);

      const startLabel = currentWindow[0];
      const endLabel = currentWindow[currentWindow.length - 1];
      const rangeLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      setDataRangeLabel(rangeLabel);

      setHasPrevious(windowStart.current > 0);
      setHasNext(windowStart.current + daysPerPage < allDays.length);
    } else {
      windowStart.current = 0;
      setCurrentDays([]);
      setDataRangeLabel('');
      setHasPrevious(false);
      setHasNext(false);
    }
  }, [allDays, paginationTrigger, daysPerPage, dayPaginationInfo, isServerPaging, data, serverOffset]);

  // Get the maximum value for color scaling
  const maxValue = useMemo(() => {
    if (data.length === 0) return 1;
    return Math.max(...data.map((d) => d.value));
  }, [data]);

  const minValue = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.min(...data.map((d) => d.value));
  }, [data]);

  // Function to get color intensity based on value
  const getColorIntensity = (value: number) => {
    if (maxValue === minValue) return 0.5;
    return (value - minValue) / (maxValue - minValue);
  };

  // Function to get background color based on intensity
  // In the ProductivityHeatMapChart component, update the getBackgroundColor function:

  // Function to get background color based on intensity
  const getBackgroundColor = (value: number) => {
    const intensity = getColorIntensity(value);

    // Using the same gradient colors as CommonHeatmapChart
    // #FDEDED (0%) -> #F8BFBF (20%) -> #F28C8C (40%) -> #E65C5C (60%) -> #D94343 (80%) -> #C83333 (100%)
    if (intensity <= 0.2) {
      // Interpolate between #FDEDED and #F8BFBF
      const localIntensity = intensity / 0.2;
      const r = Math.floor(253 - (253 - 248) * localIntensity);
      const g = Math.floor(237 - (237 - 191) * localIntensity);
      const b = Math.floor(237 - (237 - 191) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.4) {
      // Interpolate between #F8BFBF and #F28C8C
      const localIntensity = (intensity - 0.2) / 0.2;
      const r = Math.floor(248 - (248 - 242) * localIntensity);
      const g = Math.floor(191 - (191 - 140) * localIntensity);
      const b = Math.floor(191 - (191 - 140) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.6) {
      // Interpolate between #F28C8C and #E65C5C
      const localIntensity = (intensity - 0.4) / 0.2;
      const r = Math.floor(242 - (242 - 230) * localIntensity);
      const g = Math.floor(140 - (140 - 92) * localIntensity);
      const b = Math.floor(140 - (140 - 92) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else if (intensity <= 0.8) {
      // Interpolate between #E65C5C and #D94343
      const localIntensity = (intensity - 0.6) / 0.2;
      const r = Math.floor(230 - (230 - 217) * localIntensity);
      const g = Math.floor(92 - (92 - 67) * localIntensity);
      const b = Math.floor(92 - (92 - 67) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Interpolate between #D94343 and #C83333
      const localIntensity = (intensity - 0.8) / 0.2;
      const r = Math.floor(217 - (217 - 200) * localIntensity);
      const g = Math.floor(67 - (67 - 51) * localIntensity);
      const b = Math.floor(67 - (67 - 51) * localIntensity);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Find data for specific vehicle and day
  const findDataPoint = (vehicleId: string, day: string) => {
    return data.find((d) => d.vehicleId === vehicleId && d.day === day);
  };

  // Handle mouse enter for tooltip
  const handleMouseEnter = (
    event: React.MouseEvent,
    vehicleId: string,
    day: string,
    value: number,
    displayValue: string,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      vehicleId,
      day,
      value: displayValue,
      idleTime: `${displayValue} hours`,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  // Handle mouse leave for tooltip
  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const handleTableOpen = () => {
    if (onTableIconClick) {
      onTableIconClick();
    }
    setIsTableOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (editData: any) => {
    if (onCardDataUpdate && chartId) {
      onCardDataUpdate(chartId, editData.startDate, editData.endDate);
      const startDateFormatted = new Date(editData.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(editData.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  // Prepare table data if not provided
  const preparedTableData = useMemo(() => {
    if (tableData && tableData.length > 0) return tableData;

    return data.map((item) => ({
      vehicleId: item.vehicleId,
      record_date: item.record_date || item.sortDate,
      displayValue: item.displayValue,
      day: item.day,
      value: item.value,
    }));
  }, [data, tableData]);

  return (
    <>
      <Box
        className={`w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white ${containerClassName}`}
      >
        {/* Header */}
        <Box className="flex items-center justify-between w-full mb-4">
          <Box className="flex items-center gap-2">
            <Typography
              variant="h6"
              fontSize={18}
              sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
            >
              {title}
            </Typography>
            {infoIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 16,
                  height: 16,
                }}
              >
                {infoIcon}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '40px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.3,
                cursor: 'pointer',
                height: '40px',
                flexShrink: 0,
                pt: '10px',
              }}
              onClick={handleTableOpen}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
              </Box>
              <Typography
                variant="body2"
                fontSize={14}
                sx={(theme) => ({
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  height: '40px',
                })}
              >
                Table
              </Typography>
            </Box>
            <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  sx={{
                    p: 0,
                    width: '24px !important',
                    height: '24px !important',
                    minWidth: '24px !important',
                    minHeight: '24px !important',
                    maxWidth: '24px !important',
                    maxHeight: '24px !important',
                    flexShrink: 0,
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={handleMenuClick}
                >
                  <img
                    src={menuIcon}
                    alt="Menu"
                    width="24"
                    height="24"
                    style={{
                      display: 'block',
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      minHeight: '24px',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '160px',
            },
          }}
        >
          <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
            <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
            <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleEditData}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
          >
            <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleResetChanges}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
          >
            <img
              src={RestartAltIcon}
              alt="Reset"
              style={{
                width: 20,
                height: 20,
                filter:
                  'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
              }}
            />
            <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
          </MenuItem>
        </Menu>

        {/* Heatmap */}
        <Box className="w-full relative">
          {/* Color scale legend with min-max labels */}
          {data.length > 0 && (
            <Box className="flex items-center justify-end mb-2 gap-2">
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                {minValue.toFixed(1)}
              </Typography>
              <Box
                sx={{
                  width: '180px',
                  height: '12px',
                  borderRadius: '8px',
                  background:
                    'linear-gradient(90deg, #FDEDED 0%, #F8BFBF 20%, #F28C8C 40%, #E65C5C 60%, #D94343 80%, #C83333 100%)',
                }}
              />
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                {maxValue.toFixed(1)}
              </Typography>
            </Box>
          )}

          {/* No Data Message */}
          {data.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px',
                width: '100%',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          ) : (
            <>
              {/* Main container with Vehicles label */}
              <Box className="flex items-center">
                {/* Vehicles label on the left */}
                <Box className="mr-1">
                  <Typography
                    variant="caption"
                    sx={{
                      transform: 'rotate(-90deg)',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      color: '#1B1B1B',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      width: '10px',
                    }}
                  >
                    Vehicle ID
                  </Typography>
                </Box>

                {/* Grid Container */}
                <Box className="flex-1 max-w-full overflow-x-auto">
                  <Box
                    className="gap-1"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                      gridTemplateRows: `repeat(${allVehicles.length}, 1fr)`,
                      alignItems: 'center',
                    }}
                  >
                    {/* Data rows - vehicles on left, data cells for each day */}
                    {allVehicles.map((vehicleId) => (
                      <React.Fragment key={vehicleId}>
                        {/* Vehicle ID label on the left */}
                        <Box
                          className="p-2 text-center flex items-center justify-end pr-3"
                          sx={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '18px',
                            letterSpacing: '0%',
                            color: '#767676',
                            minWidth: '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={vehicleId}
                        >
                          {vehicleId}
                        </Box>

                        {/* Data cells for each day */}
                        {currentDays.map((day) => {
                          const dataPoint = findDataPoint(vehicleId, day);
                          const value = dataPoint?.value || 0;
                          const displayValue = dataPoint?.displayValue || '0.0';

                          return (
                            <Box
                              key={`${vehicleId}-${day}`}
                              className="p-2 text-center flex items-center justify-center"
                              sx={{
                                backgroundColor: getBackgroundColor(value),
                                border: '1px solid #E5E7EB',
                                borderRadius: '5px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: getColorIntensity(value) > 0.5 ? 'white' : '#374151',
                                minHeight: 32,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.05)',
                                  transition: 'all 0.2s ease-in-out',
                                },
                                minWidth: '40px',
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, vehicleId, day, value, displayValue)}
                              onMouseLeave={handleMouseLeave}
                            >
                              {displayValue}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </Box>

                  {/* Bottom row with days */}
                  <Box
                    className="gap-1 mt-1"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                    }}
                  >
                    <Box sx={{ minWidth: '80px' }}></Box> {/* Empty cell for vehicle labels column */}
                    {currentDays.map((day) => (
                      <Box
                        key={day}
                        className="p-2 text-center"
                        sx={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '18px',
                          letterSpacing: '0%',
                          color: '#767676',
                          minWidth: '40px',
                        }}
                      >
                        {day}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Days label at bottom */}
              <Box className="text-center mt-2">
                <Typography variant="caption" sx={{ color: '#1B1B1B', fontSize: '12px', fontWeight: 400 }}>
                  Days
                </Typography>
              </Box>

              {(isServerPaging || allDays.length > daysPerPage) && (
                <Box className="flex justify-center items-center mt-4 gap-4">
                  <IconButton
                    size="small"
                    onClick={handlePrevious}
                    disabled={!hasPrevious}
                    sx={{
                      border: '1px solid',
                      borderColor: theme.palette.primary.main,
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      '&:disabled': {
                        opacity: 0.5,
                        borderColor: '#ccc',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
                  </IconButton>

                  {/* Page Info - Updated to show page numbers like CommonDoublePieChart */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Current page - active */}
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        backgroundColor: theme.palette.primary.main,
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: 400,
                      }}
                    >
                      {currentPage}
                    </Box>

                    {/* Next page (light) - show if there is a next page available */}
                    {(hasNext || currentPage < totalPages) && (
                      <Box
                        // onClick={() => handlePageClick(currentPage + 1)}
                        sx={{
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: '#808080',
                          fontSize: '12px',
                          fontWeight: 400,
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                          },
                        }}
                      >
                        {currentPage + 1}
                      </Box>
                    )}

                    {/* If there are more pages beyond next, show ellipsis + last page */}
                    {totalPages > currentPage + 1 && (
                      <>
                        <Typography sx={{ color: '#808080', fontSize: '12px' }}>...</Typography>
                        <Box
                          // onClick={() => handlePageClick(totalPages)}
                          sx={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: totalPages === currentPage ? '#FFFFFF' : '#808080',
                            backgroundColor:
                              totalPages === currentPage ? theme.palette.primary.main : 'transparent',
                            fontSize: '12px',
                            fontWeight: 400,
                            '&:hover': {
                              backgroundColor:
                                totalPages === currentPage ? theme.palette.primary.main : '#F5F5F5',
                            },
                          }}
                        >
                          {totalPages}
                        </Box>
                      </>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={handleNext}
                    disabled={!hasNext}
                    sx={{
                      border: '1px solid',
                      borderColor: theme.palette.primary.main,
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      '&:disabled': {
                        opacity: 0.5,
                        borderColor: '#ccc',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Tooltip */}
        {tooltip && (
          <Box
            sx={{
              position: 'fixed',
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateX(-50%) translateY(-100%)',
              backgroundColor: 'white',
              color: 'black',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              zIndex: 1000,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Box sx={{ marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: 'black' }}>
              {tooltip.vehicleId}, {tooltip.day}
            </Box>
            <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'black' }}>{tooltip.idleTime}</Box>
          </Box>
        )}

        {/* Table Modal */}
        <Modal
          open={isTableOpen}
          onClose={() => setIsTableOpen(false)}
          aria-labelledby="idle-time-table-modal"
          aria-describedby="idle-time-table-description"
        >
          <Box
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'auto',
              height: '90vh',
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CommonAnalysisTable
              title={tableTitle}
              headerText={tableHeaderText}
              columns={tableColumns}
              data={preparedTableData}
              noDataMessage="No idle time data available"
              onClose={() => setIsTableOpen(false)}
              paginationInfo={paginationInfo}
              onPaginationChange={onPaginationChange}
            />
          </Box>
        </Modal>

        {/* Expanded Heatmap Modal */}
        <Modal
          open={isExpandedModalOpen}
          onClose={handleCloseExpandedModal}
          aria-labelledby="expanded-heatmap-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: { xs: '90%', sm: '85%', md: '80%', lg: '80%' },
              maxWidth: { xs: 525, sm: 840, md: 1050, lg: 1155 },
              maxHeight: { xs: '85vh', sm: '90vh', md: '95vh' },
              bgcolor: 'white',
              borderRadius: '16px',
              boxShadow: 24,
              p: { xs: 2, sm: 2.5, md: 3 },
              outline: 'none',
              overflow: 'visible',
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: { xs: 1.5, sm: 2 },
                position: 'relative',
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: { xs: '18px', sm: '24px', md: '28px' },
                    lineHeight: { xs: '27px', sm: '36px', md: '42px' },
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                  }}
                >
                  {title}
                </Typography>
                {infoIcon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Edit Chart Button */}
                <Box
                  onClick={handleEditData}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    colour: theme.palette.primary.main,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(85, 71, 221, 0.1)',
                    },
                  }}
                >
                  <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '21px',
                      letterSpacing: '0%',
                      color: theme.palette.primary.main,
                    }}
                  >
                    Edit Chart
                  </Typography>
                </Box>

                {/* Collapse Button */}
                <Box
                  onClick={handleCloseExpandedModal}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(85, 71, 221, 0.1)',
                    },
                  }}
                >
                  <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '21px',
                      letterSpacing: '0%',
                      color: theme.palette.primary.main,
                    }}
                  >
                    Collapse
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Expanded Heatmap Container */}
            <Box
              sx={{
                width: '100%',
                mt: { xs: 2, sm: 2.5, md: 3 },
                position: 'relative',
                overflowY: 'auto',
                overflowX: 'visible',
                maxHeight: 'calc(85vh - 120px)',
                paddingRight: '8px',
              }}
            >
              {/* Color scale legend with min-max labels */}
              {data.length > 0 && (
                <Box className="flex items-center justify-end mb-4 gap-2">
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                    {minValue.toFixed(1)}
                  </Typography>
                  <Box
                    sx={{
                      width: '240px',
                      height: '16px',
                      borderRadius: '8px',
                      background:
                        'linear-gradient(90deg, #FDEDED 0%, #F8BFBF 20%, #F28C8C 40%, #E65C5C 60%, #D94343 80%, #C83333 100%)',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px', fontWeight: 400 }}>
                    {maxValue.toFixed(1)}
                  </Typography>
                </Box>
              )}

              {/* No Data Message */}
              {data.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    width: '100%',
                  }}
                >
                  <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
                </Box>
              ) : (
                <>
                  {/* Main container with Vehicles label */}
                  <Box className="flex items-center">
                    {/* Vehicles label on the left */}
                    <Box className="mr-2">
                      <Typography
                        variant="caption"
                        sx={{
                          transform: 'rotate(-90deg)',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          textAlign: 'center',
                          color: '#1B1B1B',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          width: '14px',
                        }}
                      >
                        Vehicle ID
                      </Typography>
                    </Box>

                    {/* Grid Container */}
                    <Box className="flex-1">
                      <Box
                        className="gap-2"
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                          gridTemplateRows: `repeat(${allVehicles.length}, 1fr)`,
                          alignItems: 'center',
                        }}
                      >
                        {allVehicles.map((vehicleId) => (
                          <React.Fragment key={vehicleId}>
                            {/* Vehicle ID label on the left */}
                            <Box
                              className="p-2 text-center flex items-center justify-end pr-3"
                              sx={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '18px',
                                letterSpacing: '0%',
                                color: '#767676',
                                minWidth: '100px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={vehicleId}
                            >
                              {vehicleId}
                            </Box>

                            {/* Data cells for each day */}
                            {currentDays.map((day) => {
                              const dataPoint = findDataPoint(vehicleId, day);
                              const value = dataPoint?.value || 0;
                              const displayValue = dataPoint?.displayValue || '0.0';

                              return (
                                <Box
                                  key={`${vehicleId}-${day}`}
                                  className="p-2 text-center flex items-center justify-center"
                                  sx={{
                                    backgroundColor: getBackgroundColor(value),
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '5px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: getColorIntensity(value) > 0.5 ? 'white' : '#374151',
                                    minHeight: 40,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 0.8,
                                      transform: 'scale(1.05)',
                                      transition: 'all 0.2s ease-in-out',
                                    },
                                  }}
                                  onMouseEnter={(e) =>
                                    handleMouseEnter(e, vehicleId, day, value, displayValue)
                                  }
                                  onMouseLeave={handleMouseLeave}
                                >
                                  {displayValue}
                                </Box>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </Box>

                      {/* Bottom row with days */}
                      <Box
                        className="gap-2 mt-2"
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: `auto repeat(${currentDays.length}, 1fr)`,
                        }}
                      >
                        <Box sx={{ minWidth: '100px' }}></Box>
                        {currentDays.map((day) => (
                          <Box
                            key={day}
                            className="p-2 text-center"
                            sx={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '18px',
                              letterSpacing: '0%',
                              color: '#767676',
                            }}
                          >
                            {day}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {/* Days label at bottom */}
                  <Box className="text-center mt-4">
                    <Typography
                      variant="caption"
                      sx={{ color: '#1B1B1B', fontSize: '12px', fontWeight: 400 }}
                    >
                      Days
                    </Typography>
                  </Box>

                  {(isServerPaging || allDays.length > daysPerPage) && (
                    <Box className="flex justify-center items-center mt-4 gap-4">
                      <IconButton
                        size="small"
                        onClick={handlePrevious}
                        disabled={!hasPrevious}
                        sx={{
                          border: '1px solid',
                          borderColor: theme.palette.primary.main,
                          borderRadius: '8px',
                          width: '32px',
                          height: '32px',
                          '&:disabled': {
                            opacity: 0.5,
                            borderColor: '#ccc',
                            cursor: 'not-allowed',
                          },
                        }}
                      >
                        <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
                      </IconButton>

                      {/* Page Info - Updated to show page numbers like CommonDoublePieChart */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Current page - active */}
                        <Box
                          sx={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            backgroundColor: theme.palette.primary.main,
                            color: '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: 400,
                          }}
                        >
                          {currentPage}
                        </Box>

                        {/* Next page (light) - show if there is a next page available */}
                        {(hasNext || currentPage < totalPages) && (
                          <Box
                            // onClick={() => handlePageClick(currentPage + 1)}
                            sx={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              color: '#808080',
                              fontSize: '12px',
                              fontWeight: 400,
                              '&:hover': {
                                backgroundColor: '#F5F5F5',
                              },
                            }}
                          >
                            {currentPage + 1}
                          </Box>
                        )}

                        {/* If there are more pages beyond next, show ellipsis + last page */}
                        {totalPages > currentPage + 1 && (
                          <>
                            <Typography sx={{ color: '#808080', fontSize: '12px' }}>...</Typography>
                            <Box
                              // onClick={() => handlePageClick(totalPages)}
                              sx={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: totalPages === currentPage ? '#FFFFFF' : '#808080',
                                backgroundColor:
                                  totalPages === currentPage ? theme.palette.primary.main : 'transparent',
                                fontSize: '12px',
                                fontWeight: 400,
                                '&:hover': {
                                  backgroundColor:
                                    totalPages === currentPage ? theme.palette.primary.main : '#F5F5F5',
                                },
                              }}
                            >
                              {totalPages}
                            </Box>
                          </>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={handleNext}
                        disabled={!hasNext}
                        sx={{
                          border: '1px solid',
                          borderColor: theme.palette.primary.main,
                          borderRadius: '8px',
                          width: '32px',
                          height: '32px',
                          '&:disabled': {
                            opacity: 0.5,
                            borderColor: '#ccc',
                            cursor: 'not-allowed',
                          },
                        }}
                      >
                        <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Tooltip for Expanded Modal */}
            {tooltip && (
              <Box
                sx={{
                  position: 'fixed',
                  left: tooltip.x,
                  top: tooltip.y,
                  transform: 'translateX(-50%) translateY(-100%)',
                  backgroundColor: 'white',
                  color: 'black',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  zIndex: 10000,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Box sx={{ marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: 'black' }}>
                  {tooltip.vehicleId}, {tooltip.day}
                </Box>
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'black' }}>{tooltip.idleTime}</Box>
              </Box>
            )}
          </Box>
        </Modal>

        {/* Edit Data Modal */}
        <EditDataModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          onApply={handleApplyEditData}
          onReset={() => console.log('Reset clicked')}
          cardTitle={title}
          chartId={chartId}
          resetTrigger={resetTrigger}
        />
      </Box>
    </>
  );
};

export default ProductivityHeatMapChart;
